import { Link } from 'react-router-dom';
import { Bell, CheckCircle, Mail, Shield, Clock, Zap, Flag, BookOpen, ArrowRight } from 'lucide-react';
import { useAuth } from '../lib/auth';
import AdBanner from '../components/AdBanner';
import { ARTICLES } from '../data/articles';

const features = [
  { icon: Bell, title: 'Smart Reminders', desc: 'Set reminders a day, week, or month before your deadlines.' },
  { icon: Mail, title: 'Email Notifications', desc: 'Get notified by email when your reminder date arrives.' },
  { icon: CheckCircle, title: 'Track Progress', desc: 'Mark reminders complete and stay on top of everything.' },
  { icon: Shield, title: 'Secure & Private', desc: 'Your data is safe. Sign in with email or Google.' },
  { icon: Clock, title: 'Up to 5 Reminders', desc: 'Manage up to 5 active reminders per account.' },
  { icon: Zap, title: 'Always Available', desc: 'Access your reminders from any device, anywhere.' },
];

// ─── Bank Holidays ────────────────────────────────────────────────────────────

interface Holiday {
  date: string;
  name: string;
  note?: string;
}

const UK_HOLIDAYS: Holiday[] = [
  { date: '2026-08-27', name: 'Summer Bank Holiday', note: 'England & Wales' },
  { date: '2026-11-30', name: "St Andrew's Day", note: 'Scotland' },
  { date: '2026-12-25', name: 'Christmas Day' },
  { date: '2026-12-26', name: 'Boxing Day' },
  { date: '2027-01-01', name: "New Year's Day" },
  { date: '2027-01-02', name: 'New Year Holiday', note: 'Scotland' },
  { date: '2027-03-17', name: "St Patrick's Day", note: 'N. Ireland' },
  { date: '2027-04-02', name: 'Good Friday' },
  { date: '2027-04-05', name: 'Easter Monday', note: 'Excl. Scotland' },
];

const US_HOLIDAYS: Holiday[] = [
  { date: '2026-09-07', name: 'Labor Day' },
  { date: '2026-10-12', name: 'Columbus Day' },
  { date: '2026-11-11', name: 'Veterans Day' },
  { date: '2026-11-26', name: 'Thanksgiving Day' },
  { date: '2026-12-25', name: 'Christmas Day' },
  { date: '2027-01-01', name: "New Year's Day" },
  { date: '2027-01-19', name: 'Martin Luther King Jr. Day' },
  { date: '2027-02-15', name: "Presidents' Day" },
];

// ─── Sports Events ────────────────────────────────────────────────────────────

interface SportEvent {
  date: string;
  name: string;
  sport: string;
  emoji: string;
}

const SPORTS_EVENTS: SportEvent[] = [
  { date: '2026-07-11', name: "Wimbledon — Ladies' Singles Final", sport: 'Tennis', emoji: '🎾' },
  { date: '2026-07-13', name: "Wimbledon — Gentlemen's Singles Final", sport: 'Tennis', emoji: '🎾' },
  { date: '2026-07-14', name: 'FIFA World Cup 2026 — Semi-Final 1', sport: 'Football', emoji: '⚽' },
  { date: '2026-07-15', name: 'FIFA World Cup 2026 — Semi-Final 2', sport: 'Football', emoji: '⚽' },
  { date: '2026-07-19', name: 'FIFA World Cup 2026 — Final', sport: 'Football', emoji: '⚽' },
  { date: '2026-07-26', name: 'Tour de France — Final Stage (Paris)', sport: 'Cycling', emoji: '🚴' },
  { date: '2026-08-15', name: 'Premier League 2026/27 Season Begins', sport: 'Football', emoji: '⚽' },
  { date: '2026-08-31', name: 'US Open Tennis Begins', sport: 'Tennis', emoji: '🎾' },
  { date: '2026-09-03', name: 'NFL 2026 Season Kickoff', sport: 'American Football', emoji: '🏈' },
  { date: '2026-10-20', name: 'NBA 2026/27 Season Tips Off', sport: 'Basketball', emoji: '🏀' },
  { date: '2027-02-07', name: 'Super Bowl LXI', sport: 'American Football', emoji: '🏈' },
];

// ─── World Festivals ──────────────────────────────────────────────────────────

interface Festival {
  date: string;
  name: string;
  origin: string;
  emoji: string;
  approx?: boolean;
}

const WORLD_FESTIVALS: Festival[] = [
  { date: '2026-08-25', name: 'Mawlid al-Nabi', origin: 'Islamic', emoji: '🌙', approx: true },
  { date: '2026-08-27', name: 'Onam (Thiruvonam)', origin: 'Hindu · Kerala', emoji: '🌸', approx: true },
  { date: '2026-09-16', name: 'Ganesh Chaturthi', origin: 'Hindu', emoji: '🐘', approx: true },
  { date: '2026-09-20', name: 'Rosh Hashanah', origin: 'Jewish', emoji: '✡️' },
  { date: '2026-09-29', name: 'Yom Kippur', origin: 'Jewish', emoji: '✡️' },
  { date: '2026-10-08', name: 'Navaratri begins', origin: 'Hindu', emoji: '🪔', approx: true },
  { date: '2026-10-17', name: 'Dussehra (Vijayadashami)', origin: 'Hindu', emoji: '🪔', approx: true },
  { date: '2026-10-29', name: 'Diwali', origin: 'Hindu · Sikh · Jain', emoji: '🪔', approx: true },
  { date: '2026-10-31', name: 'Halloween', origin: 'Western', emoji: '🎃' },
  { date: '2026-11-05', name: 'Bonfire Night', origin: 'United Kingdom', emoji: '🎆' },
  { date: '2026-12-14', name: 'Hanukkah begins', origin: 'Jewish', emoji: '🕎' },
  { date: '2026-12-25', name: 'Christmas Day', origin: 'Christian · Global', emoji: '🎄' },
  { date: '2026-12-26', name: 'Kwanzaa begins', origin: 'African-American', emoji: '🕯️' },
  { date: '2027-01-14', name: 'Makar Sankranti / Pongal', origin: 'Hindu', emoji: '🌅' },
  { date: '2027-01-29', name: 'Chinese New Year (Year of the Goat)', origin: 'Chinese', emoji: '🐐' },
  { date: '2027-03-04', name: 'Holi', origin: 'Hindu', emoji: '🎨', approx: true },
  { date: '2027-03-07', name: 'Eid al-Fitr', origin: 'Islamic', emoji: '🌙', approx: true },
  { date: '2027-05-17', name: 'Eid al-Adha', origin: 'Islamic', emoji: '🌙', approx: true },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function today() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysAway(dateStr: string): number {
  const t = new Date(dateStr);
  t.setHours(0, 0, 0, 0);
  return Math.round((t.getTime() - today().getTime()) / 86_400_000);
}

function fmtDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function DaysBadge({ days }: { days: number }) {
  if (days === 0) return <span className="text-xs font-bold text-white bg-green-500 rounded-full px-2 py-0.5">Today!</span>;
  if (days < 0) return null;
  const colour = days <= 7 ? 'bg-orange-100 text-orange-700' : days <= 30 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500';
  return <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${colour}`}>{days}d</span>;
}

function upcomingOnly<T extends { date: string }>(items: T[]): T[] {
  return items.filter(i => daysAway(i.date) >= 0);
}

export default function Home() {
  const { user } = useAuth();

  const ukHols = upcomingOnly(UK_HOLIDAYS);
  const usHols = upcomingOnly(US_HOLIDAYS);
  const sports = upcomingOnly(SPORTS_EVENTS);
  const festivals = upcomingOnly(WORLD_FESTIVALS);

  return (
    <div className="space-y-16">
      {/* Hero */}
      <section className="text-center py-16 px-4">
        <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
          <Bell className="w-4 h-4" />
          Never forget anything again
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
          Reminders for
          <span className="text-blue-600"> anything</span>
        </h1>
        <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10">
          RemindMeOf lets you set smart reminders for birthdays, anniversaries, bill payments, appointments — anything that matters to you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {user ? (
            <Link to="/dashboard" className="btn-primary text-lg px-8 py-3">
              Go to My Reminders
            </Link>
          ) : (
            <>
              <Link to="/register" className="btn-primary text-lg px-8 py-3">
                Get Started Free
              </Link>
              <Link to="/login" className="btn-secondary text-lg px-8 py-3">
                Sign In
              </Link>
            </>
          )}
        </div>
      </section>

      {/* Ad - Middle */}
      <AdBanner slot="1111111111" format="rectangle" className="max-w-xl mx-auto min-h-[250px] bg-gray-100 rounded-xl" />

      {/* Features */}
      <section>
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Everything you need</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="card hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-gray-500 text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Tips & Guides ── */}
      <section>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Tips & Guides</h2>
              <p className="text-sm text-gray-400">Practical advice on reminders, memory, and staying organised</p>
            </div>
          </div>
          <Link to="/blog" className="hidden sm:flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline flex-shrink-0">
            View all articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ARTICLES.slice(0, 6).map(article => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col"
            >
              <span className="inline-block bg-blue-50 text-blue-600 text-xs font-medium px-2.5 py-1 rounded-full w-fit mb-3">
                {article.category}
              </span>
              <h3 className="font-semibold text-gray-900 leading-snug mb-2">{article.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed flex-1">{article.excerpt}</p>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:hidden">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline">
            View all articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── Bank Holidays ── */}
      <section>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
            <Flag className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Upcoming Bank Holidays</h2>
            <p className="text-sm text-gray-400">Public holidays from today · UK &amp; USA</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* UK */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-blue-600 px-5 py-3 flex items-center gap-2">
              <span className="text-xl">🇬🇧</span>
              <span className="font-semibold text-white">United Kingdom</span>
            </div>
            <ul className="divide-y divide-gray-50">
              {ukHols.map(h => {
                const days = daysAway(h.date);
                return (
                  <li key={h.date + h.name} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{h.name}</p>
                      <p className="text-xs text-gray-400">{fmtDate(h.date)}{h.note ? ` · ${h.note}` : ''}</p>
                    </div>
                    <DaysBadge days={days} />
                  </li>
                );
              })}
            </ul>
          </div>

          {/* USA */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-red-600 px-5 py-3 flex items-center gap-2">
              <span className="text-xl">🇺🇸</span>
              <span className="font-semibold text-white">United States</span>
            </div>
            <ul className="divide-y divide-gray-50">
              {usHols.map(h => {
                const days = daysAway(h.date);
                return (
                  <li key={h.date + h.name} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{h.name}</p>
                      <p className="text-xs text-gray-400">{fmtDate(h.date)}</p>
                    </div>
                    <DaysBadge days={days} />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Sports Events ── */}
      <section>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
            <span className="text-xl">🏆</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">World Sports Events</h2>
            <p className="text-sm text-gray-400">Major upcoming fixtures &amp; tournaments</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sports.map(e => {
            const days = daysAway(e.date);
            return (
              <div key={e.date + e.name} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow flex gap-3">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-xl flex-shrink-0">
                  {e.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 leading-snug">{e.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{e.sport} · {fmtDate(e.date)}</p>
                  <div className="mt-1.5">
                    <DaysBadge days={days} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── World Festivals ── */}
      <section>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <span className="text-xl">🌍</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">World Festivals &amp; Celebrations</h2>
            <p className="text-sm text-gray-400">Cultural, religious &amp; seasonal events worldwide</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {festivals.map(f => {
            const days = daysAway(f.date);
            return (
              <div key={f.date + f.name} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow flex gap-3">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-xl flex-shrink-0">
                  {f.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 leading-snug">
                    {f.name}
                    {f.approx && <span className="text-gray-400 font-normal"> *</span>}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{f.origin} · {fmtDate(f.date)}</p>
                  <div className="mt-1.5">
                    <DaysBadge days={days} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-4">* Dates marked with an asterisk are approximate and subject to moon sighting or regional variation.</p>
      </section>

      {/* CTA */}
      {!user && (
        <section className="bg-blue-600 rounded-2xl text-white text-center py-14 px-8">
          <h2 className="text-3xl font-bold mb-4">Ready to stop forgetting?</h2>
          <p className="text-blue-100 mb-8 text-lg">Join thousands of people who never miss an important date.</p>
          <Link to="/register" className="bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 px-8 rounded-lg inline-block transition-colors text-lg">
            Create Free Account
          </Link>
        </section>
      )}
    </div>
  );
}
